import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@geiertime/ui'
import { CheckCircle, Loader2, Smartphone, Download } from 'lucide-react'
import { checkoutApi } from '../api/client'

/**
 * Checkout status response from API.
 */
interface CheckoutStatusResponse {
  status: 'pending' | 'completed' | 'expired' | 'failed'
  email?: string
  temporaryPassword?: string
  licenseKey?: string
}

/**
 * Success page route.
 * Displays payment status and credentials after checkout.
 * Polls the checkout status endpoint until completion.
 */
export const Route = createFileRoute('/checkout/success')({
  component: CheckoutSuccessPage,
})

function CheckoutSuccessPage() {
  const [sessionId, setSessionId] = useState<string | null>(null)

  // Get sessionId from URL query param or Zustand store
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlSessionId = urlParams.get('sessionId')
    if (urlSessionId) {
      setSessionId(urlSessionId)
    }
  }, [])

  // Poll checkout status every 3 seconds
  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
  } = useQuery<CheckoutStatusResponse, Error>({
    queryKey: ['checkout', 'status', sessionId || ''],
    queryFn: async () => {
      if (!sessionId) {
        return { status: 'pending' as const }
      }
      const response = await checkoutApi.getStatus(sessionId)
      return response.data as CheckoutStatusResponse
    },
    refetchInterval: (query) => {
      const currentStatus = query.state.data?.status
      if (currentStatus === 'completed' || currentStatus === 'expired') {
        return false
      }
      return 3000 // 3 seconds
    },
    enabled: !!sessionId,
  })

  const status = statusData

  if (!sessionId) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-md text-center space-y-6">
          <div className="rounded-full bg-yellow-500/10 p-4 w-fit mx-auto">
            <Loader2 className="h-8 w-8 text-yellow-500 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold">Sessione non trovata</h1>
          <p className="text-muted-foreground">
            Non è stato possibile identificare la sessione di checkout.
            Controlla l'URL o contatta il supporto.
          </p>
          <Button asChild className="w-full">
            <Link to="/">Torna alla Home</Link>
          </Button>
        </div>
      </div>
    )
  }

  if (statusLoading || status?.status === 'pending') {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-md text-center space-y-6">
          <div className="rounded-full bg-primary/10 p-4 w-fit mx-auto">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
          <h1 className="text-2xl font-bold">Verifica Pagamento...</h1>
          <p className="text-muted-foreground">
            Stiamo verificando lo stato del tuo pagamento.
            Attendi qualche istante.
          </p>
          <div className="text-sm text-muted-foreground">
            Sessione: {sessionId.slice(0, 8)}...
          </div>
        </div>
      </div>
    )
  }

  if (statusError || status?.status === 'expired' || status?.status === 'failed') {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-md text-center space-y-6">
          <div className="rounded-full bg-destructive/10 p-4 w-fit mx-auto">
            <Loader2 className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold">Pagamento Non Completato</h1>
          <p className="text-muted-foreground">
            {status?.status === 'expired'
              ? 'La sessione di pagamento è scaduta. Riprova con un nuovo checkout.'
              : 'Si è verificato un errore durante il pagamento. Riprova.'}
          </p>
          <Button asChild className="w-full">
            <Link to="/">Torna alla Home</Link>
          </Button>
        </div>
      </div>
    )
  }

  if (status?.status === 'completed') {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-md space-y-6">
          {/* Success Header */}
          <div className="text-center space-y-4">
            <div className="rounded-full bg-green-500/10 p-4 w-fit mx-auto">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold">Pagamento Completato!</h1>
            <p className="text-muted-foreground">
              Grazie per il tuo acquisto. Ecco i dettagli del tuo account.
            </p>
          </div>

          {/* Credentials Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Credenziali di Accesso</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <div className="p-3 bg-muted rounded-md font-mono text-sm">
                  {status.email}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Password Temporanea</label>
                <div className="p-3 bg-muted rounded-md font-mono text-sm">
                  {status.temporaryPassword}
                </div>
                <p className="text-xs text-muted-foreground">
                  Accedi e cambia la password il prima possibile.
                </p>
              </div>
              {status.licenseKey && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Chiave di Licenza</label>
                  <div className="p-3 bg-muted rounded-md font-mono text-sm">
                    {status.licenseKey}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Download Apps */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Scarica l'App</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Scarica GeierTime sul tuo dispositivo per iniziare.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    window.open(
                      'https://play.google.com/store/apps/details?id=com.geiertime',
                      '_blank'
                    )
                  }
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  Play Store
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    window.open(
                      'https://apps.apple.com/app/geiertime',
                      '_blank'
                    )
                  }
                >
                  <Download className="h-4 w-4 mr-2" />
                  App Store
                </Button>
              </div>
            </CardContent>
          </Card>

          <Button asChild className="w-full">
            <Link to="/">Torna alla Home</Link>
          </Button>
        </div>
      </div>
    )
  }

  return null
}
